import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { formatUSPhoneNumber } from "src/services/format";
import * as Url from "src/services/url";
import type { GuardianEntry } from "./useGuardianEntries";

interface GuardianChipProps {
  entry: GuardianEntry;
  // The student being created, for a new guardian's "Parent of" line.
  studentName: string;
  onRemove: (id: string) => void;
}

export function GuardianChip({
  entry,
  studentName,
  onRemove,
}: GuardianChipProps) {
  const organization = useOrganization();
  const { glossaryTransform } = useGlossary();
  const fullName = [entry.firstName, entry.lastName].filter(Boolean).join(" ");

  const contactParts = [
    entry.phoneNumber ? formatUSPhoneNumber(entry.phoneNumber) : null,
    entry.emailAddress,
  ].filter(Boolean);

  return (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      padding={4}
      gap={2.5}
      direction="row"
      align="flex-start"
    >
      <Box flex="1">
        <Text fontSize="md" fontWeight="700">
          {fullName}
        </Text>
        <Text fontSize="sm" color="gray.700">
          {contactParts.join(" · ")}
          {renderParentOf()}
        </Text>
        {entry.kind === "existing" && (
          <Text fontSize="sm" color="gray.700">
            ID: {entry.id}
          </Text>
        )}
      </Box>
      <Button
        type="button"
        variant="outline"
        colorScheme="gray"
        size="sm"
        p="0"
        onClick={() => onRemove(entry.id)}
        aria-label={glossaryTransform("Remove parent or guardian")}
      >
        <RiCloseLine />
      </Button>
    </Flex>
  );

  function renderParentOf() {
    const separator = contactParts.length > 0 ? " · " : "";

    if (entry.kind === "new") {
      if (!studentName) return null;
      return `${separator}Parent of ${studentName}`;
    }

    if (entry.children.length === 0) return null;
    return (
      <>
        {separator}Parent of{" "}
        {entry.children.map((child, index) => (
          <React.Fragment key={child.id}>
            {index > 0 && ", "}
            <PersonNameLink
              to={Url.OrgAdmin.Students.edit(organization, child.id)}
            >
              {[child.firstName, child.lastName].filter(Boolean).join(" ")}
            </PersonNameLink>
          </React.Fragment>
        ))}
      </>
    );
  }
}
